import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Heart, Star, Users } from 'lucide-react';
import hindPhoto from '../assets/hind_1.jpg';

export const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="accueil"
      className="relative min-h-screen flex items-center hero-gradient overflow-hidden"
    >
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-accent/10 rounded-full blur-3xl animate-float animation-delay-200" />
        <div className="absolute top-1/2 left-1/3 w-40 h-40 bg-secondary/30 rounded-full blur-2xl animate-pulse-soft" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="text-center lg:text-left order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 animate-fade-in-up">
              <Heart className="w-4 h-4" />
              <span>Cabinet à Marrakech, Maroc</span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mb-6 animate-fade-in-up animation-delay-100">
              Psychomotricité –{' '}
              <span className="text-gradient">accompagner l'enfant</span>{' '}
              avec douceur et méthode
            </h1>

            <p className="text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-8 animate-fade-in-up animation-delay-200">
              Une approche personnalisée et bienveillante pour accompagner le développement psychomoteur de votre enfant. Ensemble, cultivons son potentiel.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-300">
              <Button
                variant="hero"
                size="xl"
                onClick={() => scrollToSection('#contact')}
              >
                Prendre rendez-vous
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button
                variant="hero-outline"
                size="xl"
                onClick={() => scrollToSection('#accompagnements')}
              >
                Découvrir l'accompagnement
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-6 justify-center lg:justify-start mt-12 pt-8 border-t border-border/50 animate-fade-in-up animation-delay-400">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Star className="w-5 h-5 text-primary" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-foreground">Expertise</p>
                  <p className="text-xs text-muted-foreground">Diplômée d'État</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-accent-foreground" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-foreground">Bienveillance</p>
                  <p className="text-xs text-muted-foreground">Approche douce</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <Users className="w-5 h-5 text-secondary-foreground" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-foreground">Collaboration</p>
                  <p className="text-xs text-muted-foreground">Parents & école</p>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative order-1 lg:order-2 animate-fade-in-up">
            <div className="relative aspect-[4/5] max-w-md mx-auto lg:max-w-none">
              {/* Image placeholder - Replace with actual photo of Hind */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary/20 to-accent/20 shadow-elevated overflow-hidden">
                {/* 
                  PLACEHOLDER: Replace this with actual image of Hind Lirmaqui
                  Add the image to src/assets/hind.jpg and uncomment the img tag below
                */}
                <img
                  src={hindPhoto}
                  alt="Hind Lirmaqui - Psychomotricienne"
                  className="w-full h-full object-cover"
                />
                {/*<div className="w-full h-full flex items-center justify-center bg-primary-light/50">
                  <div className="text-center p-8">
                    <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
                      <svg
                        className="w-12 h-12 text-primary"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Photo de Hind Lirmaqui
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      Remplacer par: src/assets/hind.jpg
                    </p>
                  </div>
                </div>*/}

                <img
                  src={hindPhoto}
                  alt="Hind Lirmaqui - Psychomotricienne"
                  className="w-full h-full object-cover"
                />

              </div>

              {/* Decorative floating card */}
              <div className="absolute -bottom-6 -left-6 bg-card rounded-2xl p-4 shadow-card animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">+100</p>
                    <p className="text-xs text-muted-foreground">Familles accompagnées</p>
                  </div>
                </div>
              </div>

              {/* Another decorative element */}
              <div className="absolute -top-4 -right-4 bg-card rounded-xl p-3 shadow-soft animate-float animation-delay-300">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-1">
                    <div className="w-6 h-6 rounded-full bg-primary/30" />
                    <div className="w-6 h-6 rounded-full bg-accent/50" />
                    <div className="w-6 h-6 rounded-full bg-secondary" />
                  </div>
                  <Star className="w-4 h-4 text-warning fill-warning" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;